/**
 * Study Mode Integrations component
 */

import {Container} from '../ui/Container';
import {Section} from '../ui/Section';
import {useScrollAnimation} from '../../hooks/useScrollAnimation';

interface IntegrationGroup {
  title: string;
  integrations: {
    name: string;
    description: string;
  }[];
}

const integrationGroups: IntegrationGroup[] = [
  {
    title: 'Cloud Storage',
    integrations: [
      {
        name: 'Google Drive',
        description: 'Import documents directly',
      },
      {
        name: 'Dropbox',
        description: 'Access files from Dropbox',
      },
      {
        name: 'OneDrive',
        description: 'Sync with Microsoft OneDrive',
      },
    ],
  },
  {
    title: 'Note-Taking Apps',
    integrations: [
      {
        name: 'Notion',
        description: 'Sync your study pages',
      },
      {
        name: 'OneNote',
        description: 'Import your notebooks',
      },
      {
        name: 'Obsidian',
        description: 'Connect your knowledge base',
      },
    ],
  },
  {
    title: 'Study Tools',
    integrations: [
      {
        name: 'Anki',
        description: 'Export flashcards',
      },
      {
        name: 'Quizlet',
        description: 'Export study sets',
      },
      {
        name: 'RemNote',
        description: 'Sync your notes',
      },
    ],
  },
  {
    title: 'File Formats',
    integrations: [
      {
        name: 'PDF',
        description: 'Upload and analyze PDFs',
      },
      {
        name: 'Images',
        description: 'OCR for handwritten notes',
      },
      {
        name: 'Markdown',
        description: 'Import and export',
      },
    ],
  },
];

export function StudyIntegrations() {
  const {ref, isVisible} = useScrollAnimation();

  return (
    <Section id="integrations" padding="lg" background="gray" ref={ref} className="bg-backgroundSecondary dark:bg-[#252526]">
      <Container>
        <div className={`transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-4">
              Works with Your Existing Tools
            </h2>
            <p className="text-lg text-textSecondary dark:text-[#858585] max-w-2xl mx-auto">
              Connect Study Mode with the tools you already use
            </p>
          </div>

          {/* Integrations by Category */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {integrationGroups.map((group, groupIndex) => (
              <div
                key={group.title}
                className={`transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{transitionDelay: `${groupIndex * 0.1}s`}}
              >
                <h3 className="text-sm font-semibold text-textPrimary dark:text-[#CCCCCC] mb-4 uppercase tracking-wide">
                  {group.title}
                </h3>
                <div className="space-y-3">
                  {group.integrations.map((integration) => (
                    <div
                      key={integration.name}
                      className="bg-background dark:bg-[#1E1E1E] rounded-lg p-4 border border-gray-200 dark:border-gray-700 hover:border-orange-500/30 dark:hover:border-orange-500/30 transition-colors"
                    >
                      <h4 className="font-medium text-textPrimary dark:text-[#CCCCCC] text-sm mb-1">
                        {integration.name}
                      </h4>
                      <p className="text-xs text-textSecondary dark:text-[#858585]">
                        {integration.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </Section>
  );
}

